import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { configureApiClients } from '@ngsos/portal-core/api/clients';

import { initI18n } from '@/i18n';
import { getAuthSession, setAuthSession } from '@/api/auth';
import App from '@/app/App';
import { createUserManager } from '@/app/auth/oidc';
import { startDeploymentWatchdog } from '@/app/deploymentWatchdog';
import { dropCachesLeftBehindByBlazor } from '@/app/legacyBlazorCaches';
import { createWebAuthSession } from '@/auth/webAuthSession';
import { restoreExpiredSessionBeforeFirstRender } from '@/auth/restoreSession';
import { showBootstrapError } from '@/app/bootstrapError';
import { applyRememberedAgencyThemeColor } from '@/app/shell/agencyThemeColor';
import { fetchNgTopology } from '@/app/topology';
import { installBrowserActivitySources } from '@/lib/browser/browserActivitySources';
import { installConsoleCapture, primeClientHints } from '@/lib/diagnostics';
import { installUnhandledErrorReporting } from '@/lib/errors/unhandledErrors';

export async function bootstrap(): Promise<void> {
  installConsoleCapture();
  installBrowserActivitySources();
  void primeClientHints();
  void dropCachesLeftBehindByBlazor();
  startDeploymentWatchdog();
  applyRememberedAgencyThemeColor();

  const container = document.getElementById('app');
  if (!container) {
    throw new Error('Root element #app not found');
  }

  try {
    installUnhandledErrorReporting();

    const topology = await fetchNgTopology();

    const userManager = createUserManager(topology);

    await initI18n();

    configureApiClients({
      emsBaseUrl: topology.emsServerUrl,
      appleEedBaseUrl: topology.appleEedServerUrl,
      identityBaseUrl: topology.identityServerUrl,
      authSession: getAuthSession(),
    });
    const authSession = createWebAuthSession(userManager);
    setAuthSession(authSession);
    await restoreExpiredSessionBeforeFirstRender(authSession);

    createRoot(container).render(
      <StrictMode>
        <App topology={topology} userManager={userManager} />
      </StrictMode>,
    );
  } catch (error) {
    showBootstrapError(error);
  }
}
